import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { FormattedMessage, FormattedDate } from 'react-intl'
import { useCartItem } from 'lib/core/hooks/useCartItem'
import { Price } from 'lib/shared/components'
import { Spinner, Button, Toast } from 'lib/ui/components'
import { useIsXs } from 'hooks/responsive'
import { CustomCartItem as CartItem } from 'data/cartItems'
import { useCartSubscriptions } from 'hooks/cart'
import { QtyWrapper } from 'app/sofia/scenes/Cart/components/Styled'
import { Li } from './components/Styled'
import { DEFAULT_CURRENCY_CODE, PRODUCT_TYPENAME } from 'lib/core/resources/constants'
import { getPackageLabel } from 'resources/helpers'
import { PACKAGE } from 'resources/constants'
import { updateCartItem } from 'services/cart'
import { ItemOptions } from './components/ItemOptions'
import { fetchCart } from 'lib/core/data/quote/cart'
import { useCartContextProvider } from '../CartProvider'

interface Props {
  cartItem: CartItem
  isChild?: boolean
  isSampleSupply?: boolean
  sampleDuration?: string
  canRemove?: boolean
  cartId?: string
}

export const ListItem = ({ cartItem, isChild, isSampleSupply, sampleDuration, canRemove = true, cartId }: Props) => {
  const { cartItemData, setCartItemData } = useCartContextProvider()
  const isMobile = useIsXs()
  const dispatch = useDispatch()
  const {
    subscriptionItem,
    product,
    shipmentFrequency,
    variant,
    packageQty,
    itemSubtotalWithDiscount,
    associatedProduct,
    cartItemPrice
  } = useCartSubscriptions(cartItem)
  const [itemOptionsVisible, setItemOptionsVisible] = useState(false)
  const [surveyOffer] = cartItem.product.customAttribute?.survey_offer ?? []
  const isSurveyOffer = !!parseInt(surveyOffer ?? '')
  const [updateItem, { loading }] = useMutation(updateCartItem,
    {
      onCompleted: () => {
        setItemOptionsVisible(false)
      }
    })

  const {
    removeCartItem,
    setInputQuantity,
    inputQuantity,
    isUpdateFetching,
    isRemoveFetching,
    isRemoveDisabled,
    isUpdateDisabled,
    configurableOptions,
    productPath,
    minQuantity
  } = useCartItem({ cartItem })

  const productImageUrl = !isSampleSupply ? cartItem?.product.image?.default.url ?? cartItem?.product.image?.default : associatedProduct?.product.image.default.url ?? associatedProduct?.product.image.default
  const productName = isSampleSupply ? `${product.name} - ${associatedProduct?.product.name}` : product.name
  const promoQty = cartItem.promoQty
  const [packageType] = cartItem?.product?.customAttribute?.package ?? []
  const isEach = packageType === PACKAGE.EACH

  const subscriptionStartingDate = sampleDuration && new Date(new Date().setDate(new Date().getDate() + parseInt(sampleDuration)))
  const isSimpleProduct = product.typeName === PRODUCT_TYPENAME.SIMPLE
  const showItemOptions = (!isSimpleProduct || subscriptionItem) && !isSampleSupply && !isChild

  async function updateCartItemData () {
    const cartItemToUpdate = cartItemData[cartItem.id]
    await updateItem({
      variables: {
        cartId,
        cartItems: [cartItemToUpdate]
      }
    }).then(() => {
      dispatch(fetchCart(cartId ?? ''))
    }).catch((error) => {
      Toast.toastFailure({ messageId: error })
    })
  }

  return (
    <Li
      isChild={isChild}
    >
      <div className='product-data'>
        <div>
          <div className='product-image'>
            <Link to={productPath}>
              <div
                style={{
                  backgroundImage: `url(${productImageUrl})`
                }}
              />
            </Link>
          </div>
          {!isChild && canRemove && (
            <div className='product-remove'>
              <button
                onClick={removeCartItem}
                disabled={isRemoveDisabled}
              >
                <span>
                  <FormattedMessage id='cart.item.action.remove' />
                </span>
                {isRemoveFetching && (
                  <Spinner size={16} />
                )}
              </button>
            </div>
          )}
        </div>
        <div>
          <div className='product-name'>
            {productName}
          </div>
          {!isSurveyOffer && (
            <>
              {!subscriptionItem ? (
                <>
                  {packageQty && parseInt(packageQty?.[0]) > 1 && (
                    <p>
                      <strong>
                        {packageQty}
                        <span> {getPackageLabel(packageType)}</span>
                      </strong>
                    </p>
                  )}
                </>
              ) : (
                <>
                  <strong>
                    Ships every {shipmentFrequency} weeks
                    {isChild && subscriptionStartingDate && (
                      <span className='starting-note'>
                        <span>, starting</span>
                        <FormattedDate
                          value={subscriptionStartingDate}
                          month='2-digit'
                          day='2-digit'
                        />
                      </span>
                    )}
                  </strong>
                </>
              )}
            </>
          )}
          {(subscriptionItem ?? isSampleSupply) && (
            <p>
              <strong>
                {isEach ? (
                  <>{isSampleSupply ? promoQty : inputQuantity} pieces</>
                ) : (
                  <>{isSampleSupply ? promoQty : inputQuantity} {packageType} of {packageQty} pieces</>
                )}
              </strong>
            </p>
          )}
          {subscriptionItem && isMobile && (
            <p><strong>Subscription</strong></p>
          )}
          {variant && (
            <div className='product-options'>
              <ul>
                {configurableOptions?.map(attr => {
                  const { value_label: label, value_id: valueIndex } = attr
                  return (
                    <li key={valueIndex}>
                      {label}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          <div className='product-subtotal'>
            <Price
              currency={DEFAULT_CURRENCY_CODE}
              value={isSampleSupply ?? !subscriptionItem ? cartItemPrice : itemSubtotalWithDiscount}
            />
            <small>
              {subscriptionItem ? 'Per shipment' : getPackageLabel(packageType, `${packageQty}`)}
            </small>
          </div>
        </div>
        <div className='subscription-label'>
          {!isMobile && subscriptionItem && (
            <strong>Subscription</strong>
          )}
        </div>
      </div>

      <div className='product-actions'>
        {showItemOptions && (
          <div>
            {itemOptionsVisible ? (
              <ItemOptions
                sku={cartItem.product.sku}
                currentVariant={cartItem.variantProduct}
                frequency={shipmentFrequency}
                setCartItemData={setCartItemData}
                cartItemData={cartItemData}
                onSubmit={updateCartItemData}
                isLoading={loading}
                isSubscription={Boolean(subscriptionItem)}
                inputQuantity={inputQuantity}
                minQuantity={minQuantity}
                setInputQuantity={setInputQuantity}
                configurableOptions={configurableOptions}
                cartItem={cartItem}
              />
            ) : (
              <Button onClick={() => setItemOptionsVisible(true)}>Edit product</Button>
            )}
          </div>
        )}
        {!isSurveyOffer && !subscriptionItem && (
          <div className='product-quantity'>
            <>
              <label className={subscriptionItem && 'muted'}>
                {isMobile ? 'Qty' : 'Quantity'}
              </label>
              <QtyWrapper>
                <input
                  type='number'
                  value={inputQuantity}
                  min={minQuantity}
                  onFocus={e => e.target.select()}
                  onChange={e => {
                    setInputQuantity(e.target.value)
                    setCartItemData({
                      ...cartItemData,
                      [cartItem.id]: { ...cartItemData[cartItem.id], quantity: e.target.value }
                    })
                  }}
                  readOnly={!canRemove}
                />
              </QtyWrapper>
              {(canRemove || !showItemOptions) && (
                <button
                  onClick={updateCartItemData}
                  disabled={isUpdateDisabled}
                >
                  <span>
                    <FormattedMessage id='cart.item.action.update' />
                  </span>
                  {isUpdateFetching && (
                    <Spinner size={16} />
                  )}
                </button>
              )}
            </>
          </div>
        )}
      </div>
    </Li>
  )
}
